import React, { createContext, useState, useContext, useCallback, useRef, useEffect } from "react";
import api from "../../services/api";

const SalesPanelContext = createContext();

// Helper to get companyId from localStorage token
const getCompanyIdFromToken = () => {
  try {
    const token = localStorage.getItem("token");
    if (token) {
      const tokenData = JSON.parse(token);
      // JWT payload is the second part
      const payload = JSON.parse(atob(tokenData.split('.')[1]));
      return payload.companyId;
    }
  } catch {
    // Ignore errors
  }
  return null;
};

export const SalesPanelProvider = ({ children }) => {
  // Apenas modo fixado - sem modo flutuante
  // Start with false - will be set from localStorage only if PDV is enabled
  const [salesPanelOpen, setSalesPanelOpen] = useState(false);
  const [salesPanelPinned, setSalesPanelPinned] = useState(false);

  // Control if PDV is enabled based on company plan
  const [pdvEnabled, setPdvEnabled] = useState(false);
  const [pdvLoading, setPdvLoading] = useState(true);

  // Fetch plan configuration to check if PDV is enabled
  useEffect(() => {
    const fetchPlanConfig = async () => {
      try {
        const companyId = getCompanyIdFromToken();
        if (!companyId) {
          console.warn("[SalesPanel] No companyId found in token");
          setPdvEnabled(false);
          setPdvLoading(false);
          return;
        }
        const { data } = await api.get(`/companies/listPlan/${companyId}`);
        const usePdv = data?.plan?.usePdv === true;
        setPdvEnabled(usePdv);

        if (usePdv) {
          // Only restore persistence if PDV is enabled
          try {
            const saved = localStorage.getItem("salesPanelPinned");
            if (saved === "true") {
              setSalesPanelPinned(true);
            }
          } catch {
            // Ignore localStorage errors
          }
        } else {
          // PDV disabled - clear persistence and close panel
          setSalesPanelOpen(false);
          setSalesPanelPinned(false);
          try {
            localStorage.removeItem("salesPanelPinned");
          } catch {
            // Ignore localStorage errors
          }
        }
      } catch (error) {
        console.error("Error fetching plan config:", error);
        setPdvEnabled(false);
        setSalesPanelPinned(false);
      } finally {
        setPdvLoading(false);
      }
    };

    fetchPlanConfig();
  }, []);

  // Persist salesPanelPinned to localStorage (only if PDV is enabled)
  useEffect(() => {
    // Only persist if PDV is enabled and not loading
    if (pdvEnabled && !pdvLoading) {
      try {
        localStorage.setItem("salesPanelPinned", salesPanelPinned.toString());
      } catch {
        // Ignore localStorage errors
      }
    }
  }, [salesPanelPinned, pdvEnabled, pdvLoading]);

  // PDV header state (shared with external header)
  const [cartItemCount, setCartItemCount] = useState(0);
  const [cartTotal, setCartTotal] = useState(0);
  const [showQuotesEnabled, setShowQuotesEnabled] = useState(true);

  // Store selection state
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState(null);

  // PDV actions stored in ref (no re-renders when actions change)
  const pdvActionsRef = useRef({
    onCartClick: null,
    onHelpClick: null,
    onQuotesClick: null,
  });

  const openSalesPanel = useCallback(() => {
    // Only open if PDV is enabled
    if (pdvEnabled) {
      setSalesPanelPinned(true);
    }
  }, [pdvEnabled]);

  const closeSalesPanel = useCallback(() => {
    setSalesPanelOpen(false);
    setSalesPanelPinned(false);
  }, []);

  const togglePinSalesPanel = useCallback(() => {
    // Apenas fecha o painel (não há mais modo flutuante)
    setSalesPanelPinned(false);
  }, []);

  const toggleSalesPanel = useCallback(() => {
    // Only toggle if PDV is enabled
    if (pdvEnabled) {
      setSalesPanelPinned(prev => !prev);
      setSalesPanelOpen(false);
    }
  }, [pdvEnabled]);

  // Register PDV actions (called by TicketPdvPanel) - stores in ref only, NO state update
  const registerPdvActions = useCallback((actions) => {
    pdvActionsRef.current = { ...pdvActionsRef.current, ...actions };
  }, []);

  // Get current PDV actions
  const getPdvActions = useCallback(() => pdvActionsRef.current, []);

  // Update cart info (called by TicketPdvPanel)
  const cartInfoRef = useRef({ itemCount: 0, total: 0 });
  const updateCartInfo = useCallback((itemCount, total) => {
    // Only update state if values actually changed
    if (cartInfoRef.current.itemCount !== itemCount || cartInfoRef.current.total !== total) {
      cartInfoRef.current = { itemCount, total };
      setCartItemCount(itemCount);
      setCartTotal(total);
    }
  }, []);

  return (
    <SalesPanelContext.Provider
      value={{
        salesPanelOpen,
        salesPanelPinned,
        setSalesPanelOpen,
        setSalesPanelPinned,
        openSalesPanel,
        closeSalesPanel,
        togglePinSalesPanel,
        toggleSalesPanel,
        // PDV enabled state (from plan)
        pdvEnabled,
        pdvLoading,
        // PDV header state
        cartItemCount,
        cartTotal,
        showQuotesEnabled,
        setShowQuotesEnabled,
        registerPdvActions,
        updateCartInfo,
        getPdvActions,
        // Store selection
        stores,
        setStores,
        selectedStore,
        setSelectedStore,
      }}
    >
      {children}
    </SalesPanelContext.Provider>
  );
};

export const useSalesPanel = () => {
  const context = useContext(SalesPanelContext);
  if (!context) {
    throw new Error("useSalesPanel must be used within a SalesPanelProvider");
  }
  return context;
};

export default SalesPanelContext;
